// The Agences tab's rules (D-59): an account's state, a rider's status, the
// « À faire » points written from the numbers, and the small sums the screens
// share with the agency's own space. Pure functions, tested in tests/agences.test.ts.
import { LANG, S } from './strings';
import { TZ, dec1, fmtN, hhmm, xaf } from './format';
import type { Account, AgAgency, AgRider, Agences, Lane, Provider, Topup } from './types';

export type Tone = 'green' | 'amber' | 'red' | 'blue' | 'grey' | 'violet';

/** Bon, Bas, En négatif or En pause, from the balance and its pace. */
export function accountState(a: Pick<Account, 'balance' | 'paused' | 'days_to_zero'>): { key: 'ok' | 'low' | 'neg' | 'paused'; tone: Tone } {
  if (a.paused) return { key: 'paused', tone: 'red' };
  if (a.balance < 0) return { key: 'neg', tone: 'red' };
  if (a.days_to_zero != null && a.days_to_zero < 4) return { key: 'low', tone: 'amber' };
  return { key: 'ok', tone: 'green' };
}

/** What a rider is doing now, as the screens name it. */
export function riderStatus(r: Pick<AgRider, 'state' | 'busy' | 'live' | 'seen_at'>): { key: keyof typeof S.ag.rs; tone: Tone } {
  if (r.state === 'suspended') return { key: 'suspended', tone: 'grey' };
  if (r.state === 'invited') return { key: 'invited', tone: 'violet' };
  if (r.busy) return { key: 'busy', tone: 'blue' };
  if (r.live) return { key: 'free', tone: 'green' };
  if (!r.seen_at) return { key: 'never', tone: 'grey' };
  return { key: 'off', tone: 'grey' };
}

/** A rider who cannot go online: papers missing or no consent yet (C18, D-49, D-50). */
export const blocked = (r: Pick<AgRider, 'state' | 'papers_complete' | 'consent'>) =>
  r.state === 'active' && (!r.papers_complete || r.consent !== 'given');

/** « mercredi 4 novembre », a day `days` from `now`. */
export function dayIn(days: number, now = Date.now(), withWeekday = true) {
  const d = new Date(now + days * 86400000);
  return d.toLocaleDateString(LANG === 'en' ? 'en-GB' : 'fr-FR', { ...(withWeekday ? { weekday: 'long' } : {}), day: 'numeric', month: 'long', timeZone: TZ });
}
/** « vendredi 6 novembre » for a date. */
export const longDay = (iso: string, withWeekday = true) => dayIn((new Date(iso).getTime() - Date.now()) / 86400000, Date.now(), withWeekday);
/** « dimanche soir », « lundi 16 h 14 »: when a top-up happened, said simply. */
export function whenSaid(iso: string, now = Date.now()) {
  const d = new Date(iso), h = Number(d.toLocaleString('en-GB', { hour: 'numeric', hour12: false, timeZone: TZ }));
  const sameDay = d.toLocaleDateString('fr-FR', { timeZone: TZ }) === new Date(now).toLocaleDateString('fr-FR', { timeZone: TZ });
  if (LANG === 'en') {
    if (sameDay) return 'at ' + hhmm(d);
    const wd = d.toLocaleDateString('en-GB', { weekday: 'long', timeZone: TZ });
    return `${wd} ${h >= 18 ? 'evening' : h < 12 ? 'morning' : 'afternoon'}`;
  }
  if (sameDay) return 'à ' + hm(d);
  const wd = d.toLocaleDateString('fr-FR', { weekday: 'long', timeZone: TZ });
  return h >= 18 ? `${wd} soir` : h < 12 ? `${wd} matin` : `${wd} après-midi`;
}
/** « 8 h 20 » in French (no leading zero, never split across lines), « 08:20 » in English. */
export const hm = (d: string | Date) => (LANG === 'en' ? hhmm(d) : hhmm(d).replace(/^0/, '').replace(':', '\u00a0h\u00a0'));
export const netName = (p: Provider | string) => (p === 'MTN_MOMO_CMR' || p === 'MTN MoMo' ? 'MTN MoMo' : 'Orange Money');
/** The household app stores its tags in English (apps/consumer rate.tsx); the console shows them in its language. */
export const tagWord = (t: string) => S.ag.tags[t] ?? t;
export const negativeTag = (t: string) => t === 'Late' || t === 'Hard to reach';
export const signedXaf = (n: number) => (n < 0 ? '−' : '') + xaf(Math.abs(n));
export const signedN = (n: number) => (n < 0 ? '−' : '') + fmtN(Math.abs(n));
export const rating = (x: number | null) => (x == null ? null : dec1(x));
/** A list as French writes it: « a, b et c ». */
export function listWords(xs: string[]) {
  return xs.length <= 1 ? xs.join('') : xs.slice(0, -1).join(', ') + S.ag.and + xs[xs.length - 1];
}

export interface TodoItem {
  id: string; group: 'today' | 'week' | 'mygaz'; tone: 'bad' | 'mid' | 'grey' | 'blue';
  icon: 'wallet' | 'clock' | 'alert' | 'doc' | 'users' | 'lock'; title: string; body: string; who: string;
  agencyId?: string; riderId?: string; acts: ('call' | 'account' | 'file' | 'check' | 'retry')[];
  /** The compact card: whose point it is, a few words, one number, one short line. */
  name: string; head: string; figure: string | null; sub: string;
}

/** How long a balance lasts, said simply: « 12 jours », « 3 mois », « plus d’un an ». */
export function runwayWords(days: number) {
  if (days < 60) return S.ag.acct.rDays(Math.round(days));
  if (days < 365) return S.ag.acct.rMonths(Math.round(days / 30));
  return S.ag.acct.rYear;
}

/** « mer. 4 nov. », a day `days` from now, short. */
export function dayShort(days: number, now = Date.now()) {
  return new Date(now + days * 86400000).toLocaleDateString(LANG === 'en' ? 'en-GB' : 'fr-FR', { weekday: 'short', day: 'numeric', month: 'short', timeZone: TZ });
}

const lastFailed = (tops: Topup[], now: number) =>
  tops.find((t) => (t.status === 'failed' || t.status === 'rejected') && now - new Date(t.at).getTime() < 3 * 86400000);
const pending = (tops: Topup[]) => tops.find((t) => t.status === 'created' || t.status === 'accepted' || t.status === 'unknown');

/** The « À faire » points, most urgent first: accounts, then riders, then MyGaz's own checks. */
export function todo(data: Agences, now = Date.now()): TodoItem[] {
  const out: TodoItem[] = [];
  const all = data.agencies.flatMap((a) => a.riders.map((r) => ({ a, r })));
  const rated = all.filter(({ r }) => r.rating != null && r.reviews >= 3 && r.state === 'active');
  const lowest = rated.length > 1 ? rated.reduce((m, x) => ((x.r.rating ?? 9) < (m.r.rating ?? 9) ? x : m)) : null;
  const avgAll = (() => { const xs = all.filter(({ r }) => r.avg_min != null); return xs.length ? Math.round(xs.reduce((s, x) => s + (x.r.avg_min ?? 0), 0) / xs.length) : null; })();
  for (const a of data.agencies) {
    const st = accountState(a), f = lastFailed(a.topups, now), p = pending(a.topups);
    const pauseDay = a.days_to_pause != null ? dayIn(a.days_to_pause, now) : '';
    if (st.key === 'paused') {
      out.push({ id: 'paused-' + a.agency_id, group: 'today', tone: 'bad', icon: 'wallet', title: S.ag.t.paused(a.name), body: S.ag.t.pausedBody, who: a.name, agencyId: a.agency_id, acts: ['call', 'account'],
        name: a.name, head: S.ag.att.paused, figure: signedXaf(a.balance), sub: S.ag.att.pausedSub });
    } else if (st.key === 'neg') {
      const since = a.below_zero_since ? longDay(a.below_zero_since) : '';
      out.push({ id: 'neg-' + a.agency_id, group: 'today', tone: 'bad', icon: 'wallet', title: S.ag.t.neg(a.name, pauseDay),
        body: S.ag.t.negBody(signedXaf(a.balance), since) + (f ? S.ag.t.failed(xaf(f.amount), netName(f.provider), whenSaid(f.at, now)) : ''), who: a.name, agencyId: a.agency_id, acts: ['call', 'account'],
        name: a.name, head: S.ag.att.neg, figure: signedXaf(a.balance), sub: a.days_to_pause != null ? S.ag.att.pause(dayShort(a.days_to_pause, now)) : S.ag.att.pausedSub });
    } else if (st.key === 'low') {
      out.push({ id: 'low-' + a.agency_id, group: 'today', tone: 'mid', icon: 'clock', title: S.ag.t.low(a.name, dayIn(a.days_to_zero ?? 0, now)),
        body: S.ag.t.lowBody(xaf(a.fee_per_day)) + (p ? S.ag.t.pending(xaf(p.amount), netName(p.provider), hm(p.at)) : ''), who: a.name, agencyId: a.agency_id, acts: p ? ['account', 'retry'] : ['call', 'account'],
        name: a.name, head: S.ag.att.low, figure: signedXaf(a.balance), sub: S.ag.att.zero(dayShort(a.days_to_zero ?? 0, now)) + (p ? S.ag.att.waiting : '') });
    } else if (f) {
      out.push({ id: 'fail-' + a.agency_id, group: 'today', tone: 'mid', icon: 'wallet', title: S.ag.t.failedOnly(a.name, xaf(f.amount), netName(f.provider), whenSaid(f.at, now)),
        body: S.ag.t.failedOnlyBody, who: a.name, agencyId: a.agency_id, acts: ['call', 'account'],
        name: a.name, head: S.ag.att.failed, figure: xaf(f.amount), sub: `${netName(f.provider)} · ${whenSaid(f.at, now)}` });
    }
  }
  for (const { a, r } of all) {
    if (r.state !== 'active') continue;
    const isLowest = lowest?.r.id === r.id && (r.rating ?? 5) < 4.5;
    if (r.cancelled_30d >= 3 || (isLowest && r.cancelled_30d >= 1)) {
      out.push({ id: 'watch-' + r.id, group: 'today', tone: 'mid', icon: 'alert',
        title: S.ag.t.watch(r.name, r.cancelled_30d) + (isLowest ? S.ag.t.watchLow(rating(r.rating)!) : ''),
        body: (r.avg_min != null && avgAll != null && r.avg_min > avgAll + 5 ? S.ag.t.watchMin(r.avg_min, avgAll).trim() : ''), who: a.name, agencyId: a.agency_id, riderId: r.id, acts: ['file', 'call'],
        name: r.name, head: S.ag.att.cancels(r.cancelled_30d), figure: r.rating != null ? '★ ' + rating(r.rating) : null,
        sub: isLowest ? S.ag.att.lowest : r.avg_min != null ? S.ag.att.minutes(r.avg_min) : a.name });
    }
  }
  for (const { a, r } of all) {
    if (r.state === 'active' && blocked(r)) {
      const miss = r.missing.map((k) => S.ag.docs[k]);
      out.push({ id: 'off-' + r.id, group: 'week', tone: 'grey', icon: 'doc', title: S.ag.t.offline(r.name),
        body: [miss.length ? S.ag.t.missing(listWords(miss)) : '', r.consent !== 'given' ? S.ag.t.consent : ''].filter(Boolean).join(' '), who: a.name, agencyId: a.agency_id, riderId: r.id, acts: ['file'],
        name: r.name, head: S.ag.att.offline, figure: null,
        sub: [r.missing.length ? S.ag.tbl.lacks(r.missing.map((k) => S.ag.tbl.short[k]).join(', ')) : '', r.consent !== 'given' ? S.ag.att.noConsent : ''].filter(Boolean).join(' · ') });
    }
  }
  // Riders who cannot work come before invitations still waiting.
  for (const { a, r } of all) {
    if (r.state === 'invited') {
      out.push({ id: 'inv-' + r.id, group: 'week', tone: 'grey', icon: 'users', title: S.ag.t.invited(r.name), body: S.ag.t.invitedBody(longDay(r.joined_at)), who: a.name, agencyId: a.agency_id, riderId: r.id, acts: [],
        name: r.name, head: S.ag.att.invited, figure: null, sub: S.ag.att.invitedSub(longDay(r.joined_at, false)) });
    }
  }
  if (data.papers_to_check > 0) {
    out.push({ id: 'papers', group: 'mygaz', tone: 'blue', icon: 'lock', title: S.ag.t.papers(data.papers_to_check), body: S.ag.t.papersBody, who: 'MyGaz', acts: ['check'],
      name: 'MyGaz', head: S.ag.att.papers, figure: String(data.papers_to_check), sub: S.ag.att.papersSub });
  }
  const rank = { bad: 0, mid: 1, grey: 2, blue: 3 };
  const grp = { today: 0, week: 1, mygaz: 2 };
  return out.sort((x, y) => grp[x.group] - grp[y.group] || rank[x.tone] - rank[y.tone]);
}

/** The first rider whose papers wait for MyGaz's check, for « Commencer le contrôle ». */
export const firstToCheck = (data: Agences) =>
  data.agencies.flatMap((a) => a.riders).find((r) => r.state === 'active' && r.papers_complete && !r.papers_checked_at) ?? null;

/** Deliveries per hour from 7 h to 21 h (Douala time), across lanes. */
export function perHour(lanes: Lane[], from = 7, to = 21) {
  const n = Array.from({ length: to - from }, () => 0);
  for (const l of lanes) for (const d of l.done) {
    const h = Number(new Date(d.at).toLocaleString('en-GB', { hour: 'numeric', hour12: false, timeZone: TZ }));
    if (h >= from && h < to) n[h - from] += 1;
  }
  return n;
}
/** A time as a fraction of the 7 h to 21 h line, clamped. */
export function dayPos(iso: string, from = 7, to = 21) {
  const d = new Date(iso);
  const [h, m] = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ }).split(':').map(Number);
  return Math.max(0, Math.min(1, (h + m / 60 - from) / (to - from)));
}
export const laneCash = (l: Lane) => l.done.reduce((s, d) => s + d.cash, 0);
export function avgMin(lanes: Lane[]) {
  const xs = lanes.flatMap((l) => l.done.map((d) => d.min)).filter((m): m is number => m != null);
  return xs.length ? Math.round(xs.reduce((s, m) => s + m, 0) / xs.length) : null;
}
export const firstDone = (lanes: Lane[]) => lanes.flatMap((l) => l.done.map((d) => d.at)).sort()[0] ?? null;

/** The agency's top-up offers: 5 000, 10 000, 25 000 (C18), the one near 50 deliveries first. */
export function topupOffers(feePerDelivery: number) {
  const offers = [5000, 10000, 25000];
  const target = Math.max(0, feePerDelivery) * 50;
  const pick = target > 0 ? offers.reduce((b, o) => (Math.abs(o - target) < Math.abs(b - target) ? o : b), offers[0]) : 10000;
  return { offers, pick };
}

/** The CSV of the agency's statement: date, line, order, amount, balance after. */
export function statementCsv(lines: { at: string; kind: string; order_ref: string | null; quartier: string | null; rail: string | null; note: string | null; amount: number; balance_after: number }[]) {
  const esc = (v: string) => (/[",;\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const head = 'date;ligne;commande;quartier;montant_xaf;solde_apres_xaf';
  return [head, ...lines.map((l) => [new Date(l.at).toISOString(), l.kind === 'fee' ? 'frais' : l.kind === 'topup' ? `recharge ${l.rail ?? ''}`.trim() : `ajustement ${l.note ?? ''}`.trim(),
    l.order_ref ?? '', l.quartier ?? '', String(l.amount), String(l.balance_after)].map(esc).join(';'))].join('\n');
}

/** Who is ahead of whom in a table sort. */
export function sortRiders(rows: { a: AgAgency; r: AgRider }[], by: 'rating' | 'trips' | 'cancelled' | 'cash') {
  const k = (r: AgRider) => (by === 'rating' ? -(r.rating ?? -1) : by === 'trips' ? -r.trips : by === 'cancelled' ? -r.cancelled_30d : -r.cash_today);
  return [...rows].sort((x, y) => k(x.r) - k(y.r) || x.r.name.localeCompare(y.r.name, 'fr'));
}

/**
 * A smooth line through points that never overshoots them (monotone cubic,
 * Fritsch–Carlson): a balance that only falls between two days is drawn falling.
 */
export function smoothPath(pts: [number, number][]) {
  const n = pts.length;
  if (n === 0) return '';
  if (n === 1) return `M${pts[0][0]},${pts[0][1]}`;
  const dx: number[] = [], m: number[] = [];
  for (let i = 0; i < n - 1; i++) { dx[i] = pts[i + 1][0] - pts[i][0]; m[i] = dx[i] ? (pts[i + 1][1] - pts[i][1]) / dx[i] : 0; }
  const t: number[] = [m[0]];
  for (let i = 1; i < n - 1; i++) t[i] = m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2;
  t[n - 1] = m[n - 2];
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) { t[i] = 0; t[i + 1] = 0; continue; }
    const a = t[i] / m[i], b = t[i + 1] / m[i], s = a * a + b * b;
    if (s > 9) { const k = 3 / Math.sqrt(s); t[i] = k * a * m[i]; t[i + 1] = k * b * m[i]; }
  }
  let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i] / 3;
    d += ` C${(pts[i][0] + h).toFixed(1)},${(pts[i][1] + t[i] * h).toFixed(1)} ${(pts[i + 1][0] - h).toFixed(1)},${(pts[i + 1][1] - t[i + 1] * h).toFixed(1)} ${pts[i + 1][0].toFixed(1)},${pts[i + 1][1].toFixed(1)}`;
  }
  return d;
}
